import { BaseLanguage } from "./baseLang.service";
import { SemanticErrorCause, SyntaxErrorCause } from "app/xlogo-parser";

export class EnglishService extends BaseLanguage {
  //#################################################################################
  //
  // This service holds hashmap containing all german strings used in our platform
  // When translating, please keep keys (first argument) same, while
  // adapting the values (seconds argument).
  //
  //#################################################################################

  constructor() {
    super();

    this.populateStaticStrings();
    this.defineLoginStrings();
    this.defineExamStrings();
    this.defineOnlineStorageStrings();
    this.defineQickStartStrings();
    this.defineRuntimeErrorStrings();
    this.defineSemanticErrorStrings();
    this.defineSyntaxErrorStrings();
    this.defineDebugWatchStrings();
    this.defineDebugControls();
    this.defineTooltips();
    this.defineProgressStrings();
    this.defineRobotStrings();
    this.defineUIstrings();
    this.defineMenuStrings();
  }

  // strings used in menu
  private defineMenuStrings() {
    this.define("menu", "Menu");
    this.define("saveFile", "Save File");
    this.define("loadFile", "Load File");
    this.define("saveCanvas", "Save Picture");
    this.define("fullscreen", "Fullscreen");
    this.define("newfeatures", "What's new?");
    this.define("Cheatsheet", "Logo overview");
    this.define("background", "Background pictures");
    this.define("save-as-png", "Save as png");
    this.define("save-as-svg", "Save as svg");
    this.define("Robot", "Robots");
  }

  // user interface strings: history, header, input and general UI
  private defineUIstrings() {
    this.define("enterCommand", "Enter command(s) here");
    this.define("cancel", "cancel");
  }

  // Robots interaction strings for BlueBot and Root
  private defineRobotStrings() {
    this.define("WhichRobotUsed", "Which Robot do you use?");
    this.define("disconnectBlueBot", "BlueBot is already connected. Disconnect?");
    this.define("RobotConnected", "Root is already connected. Disconnect?");
    this.define("ConnectionNotPossible", "Connection not possible");
    this.define("ConnectionError", "Your  computer and/or browser does not support Bluetooth Low Energy devices. Update your Bluetooth drivers and test again with another device or browser");
    this.define("RobotDisconnect", "Yes, disconnect");
  }

  //Progress strings shown in header while execution
  private defineProgressStrings() {
    this.define("executingVisitor", "Executing command...");
    this.define("init", "Initializing...");
  }

  // Strings related to tooltips in header
  private defineTooltips() {
    this.define("tooltipButtonStop", "Stop the current program.");
    this.define("tooltipButtonDebug", "Execute your program step by step.");
    this.define("tooltipButtonForward", "Take one step backward.");
    this.define("tooltipButtonBackward", "Take one step forward.");
    this.define("tooltipButtonSpeed", "Adjust the execution speed");
    this.define("tooltipButtonLanguage", "Change the language settings");
    this.define("tooltipButtonTurtle", "Change the turtle icon");
  }

  // Strings related to debug controls in header
  private defineDebugControls() {
    this.define("buttonDebugToggle", "Step by step");
    this.define("buttonForward", "Forward");
    this.define("buttonBackward", "Backward");
  }

  // Strings related to ebug watch
  private defineDebugWatchStrings() {
    this.define("watchPenDown", "Pen down:");
    this.define("watchErasing", "Erase mode:");
    this.define("watchVisible", "Visible:");
    this.define("watchColor", "Pen Color:");
    this.define("watchWidth", "Pen Width:");
    this.define("watchDirection", "Direction:");
  }

  // Strings related to syntactic errors
  private defineSyntaxErrorStrings() {
    this.define(SyntaxErrorCause[SyntaxErrorCause.EXTRANEOUS_INPUT], "Extraneous token {extraneousToken}.");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISMATCHED_INPUT], "Invalid token {mismatchedToken}.");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISSING_INPUT], "Missing Token {missingToken} at {missingPosition}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.NO_VIABLE_ALT], "Invalid syntax at {no_viable_alt_position}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.UNKNOWN], "An unknown error occurred: {msg}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.FAILED_PREDICATE], "{msg}"); //this one should not be used. It indicates an unexpected error on our side
  }

  // Strings related to semantic errors
  private defineSemanticErrorStrings() {
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_NAME], "The name of this program is already in use above");
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_PARAMETER], "The name of this parameter is used several times: {parameter}");
    this.define(SemanticErrorCause[SemanticErrorCause.INVALID_PROGRAM_NAME], "The use of this name is not allowed for a program");
    this.define(SemanticErrorCause[SemanticErrorCause.NOT_ENOUGH_ARGUMENTS], "Not enough arguments for '{program}'. {number_of_arguments} arguments are expected.");
    this.define(SemanticErrorCause[SemanticErrorCause.TOO_MANY_ARGUMENTS], "Too many arguments for '{program}'. {number_of_arguments} arguments are expected.");
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_PROGRAM], "The program '{program}' does not exist or it contains errors.");
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_VARIABLE], "The variable or parameter '{variable}' has not been defined");
  }

  // Strings related to runtime errors
  private defineRuntimeErrorStrings() {
    this.define("runtime_error_generic_invalid_argument", "Invalid argument for '{program}': {argument}");
    this.define("runtime_error_unknown_program", "A program with the name '{program}' does not exist or it contains errors");
    this.define("runtime_error_unknown_function", "The function {function_name} does not exist. This is a bug. Please report it.");
    this.define("runtime_error_unknown_builtin", "A command with the name '{command}' does not exist. This is a bug. Please report it.");
    this.define("runtime_error_unknown_operator", "The operator '{operator}' does not exist. This is a bug. Please report it.");
    this.define("runtime_error_division_by_zero", "Division by 0 is not possible");
    this.define("runtime_error_generic_not_in_range", "Parameter for {program} must be between {lower_bound} and {upper_bound}");
    this.define("runtime_error_invalid_argument_tan", "Parameter for tan cannot be of the form (0.5 + k) * PI");
    this.define("runtime_error_mod_by_zero", "Calculating 'mod n 0' is not possible");
    this.define("runtime_error_invalid_argument_colornumber", "The specified color number does not exist: {color_number}");
    this.define("runtime_error_invalid_argument_colorname", "The specified color name does not exist: {color_name}");
    this.define("runtime_error_invalid_argument_rgb", "The values for the color must be numbers: {color}");
    this.define("runtime_error_rgb_out_of_range", "The specified color is not valid: {color}");
    this.define("runtime_error_sqrt_negative_number", "Square root of negative numbers is not possible");
    this.define("runtime_error_log10_negative_number", "Calculating logarithms of negative numbers is not possible");
    this.define("runtime_error_setpw_negative_number", "Setting a negative pen width is not possible");
    this.define("runtime_error_wait_negative_number", "Waiting a negative amount of time is not possible");
    this.define("runtime_error_circle_negative_number", "Drawing a circle with negative radius is not possible");
  }

  // Strings related to quickstarter
  private defineQickStartStrings() {
    this.define("tutorialtype", "Choose a tutorial.");
    this.define("doyouneedhelp", "Do you need help?");
    this.define("firststeps", "First steps");
    this.define("definenewcommands", "Defining new commands");
    this.define("debugger", "Step-by-step execution");
    this.define("intro1", "In this tutorial you'll learn how to program in XLogoOnline.");
    this.define("intro2", "See the nice drawing? Soon you'll know how to create drawings like these on your own.");
    this.define("intro3", "Programs are composed of commands. FD and BK (used to draw lines) as well as RT and LT (turn left or rigth) are examples of such commands.");
    this.define("intro4", "Let's give it a try. Click inside the area and hit ENTER");
    this.define("intro5", "The turtle executed your program and its results are shown here");
    this.define("intro6", "Please erase the picture using the command CS.");
    this.define("intro7", "CS erases all lines and places the turtle back into the center");
    this.define("intro8", "Progams may even contain multiple commands. Do you know what this program does?");
    this.define("intro9", "All previously issued programs are listed here.");
    this.define("intro10", "Using the ARROW-KEYS " + String.fromCharCode(8679) + " and " + String.fromCharCode(8681) + " you can retrieve old programs without the need to type everything from scratch.");
    this.define("intro11", "What do you think will happen in case of an error?");
    this.define("intro12", "This program is erroneous. Do you spot the error? Find out how the turtle copes with errors.");
    this.define("intro13", "The turtle doesn't understand our program and, instead, issued an error message in orange.");
    this.define("intro14", "Ready. Have fun writing your first program!");
    this.define("editor1", "In this tutorial you'll learn how to define and use your own commands.");
    this.define("editor2", "See this program? What do you you think it will do??");
    this.define("editor3", "Okay!? Such a complicated program, only to draw four squares...");
    this.define("editor4", "Isn't there a simpler solution, for example like this?");
    this.define("editor5", "Unfortunately not; the turtle doesn't know command QUADRAT... So let's define it!");
    this.define("editor6", "New commands are defined here. This field is called editor.");
    this.define("editor7", "'TO' QUADRAT 'END' defines a new command named QUADRAT.");
    this.define("editor8", "The same editor window can be used to define multiple commands.");
    this.define("editor9", "Oups! Do you see the red cross on line 5? I indicates an error on this particular line. Any idea? We illegally defined command QUADRAT twicet. (Hint: After this tutorial you can hover over these crosses and receive further diagnostic support)");
    this.define("editor10", "What effect do we want QUADRAT to have? We define our intended behaviour between TO and END");
    this.define("editor11", "Let's see what it does. What do you expect? Give it a try.");
    this.define("editor12", "It's a square, with only one single command! How convenient when drawing bigger pictures with multiple squares...");
    this.define("editor13", "Congratulation! Now you know how to define and invoke your own commands. Have fun!");
  }

  // Strings related to storage functionality
  private defineOnlineStorageStrings() {
    this.define("loginButton", "Login");
    this.define("logoutButton", "Logout");
    this.define("registerButton", "Register");
    this.define("loginTitle", "Login");
    this.define("username", "username");
    this.define("password", "password (optional)");
    this.define("successfulLoginTitle", "Login succeeded");
    this.define("successfulLoginText", "You are now logged in as ");
    this.define("failLoginTitle", "Login failed");
    this.define("tryAgain", "Try Again!");
    this.define("registerTitle", "Register New User");
    this.define("repeatPassword", "repeat password");
    this.define("passwordMisatchTitle", "Mismatch");
    this.define("passwordMisatchText", "The two passwords you provided do not match. Do you want to try again?");
    this.define("successfulRegistrationTitle", "Registration succeeded");
    this.define("successfulRegistrationText", "Congratulations! You have been successfully registrated and are now logged in as ");
    this.define("serversuccess", "Success");
    this.define("unknownuser", "This username is not known to the server");
    this.define("wrongpassword", "You provided the wrong password for the chosen username");
    this.define("usernamealreadytaken", "This username already exists. Choose another one.");
    this.define("Ihaveread", "I have read and agree to the ");
    this.define("datenschutz", "Privacy Policy");
    this.define("aswellas", "and the");
    this.define("terms", "terms and conditions");
    this.define("readandaccepted", "");
    this.define("toomuchtext", "This document is too big");
    this.define("toomuchtexthint", "Hint: Why not splitting your solution into smaller chunks?");
    this.define("documentexists", "Document exists");
    this.define("overwrite", "Do you want to overwrite it?");
    this.define("writtensuccessfully", "Saved successfully");
    this.define("doYouWantToLogOut", "Do you want to log out?");
    this.define("didYouSaveYourPrograms", "Did you save your programs?");
    this.define("announceTitle", "Enrolment");
  }

  // Strings related to Exam mode
  private defineExamStrings() {
    this.define("handInTask", "Hand in current task");
    this.define("choseAnOption", "Chose one of the options below");
    this.define("startExamMode", "Brain teaser");
    this.define("waitingForExamToStart", "Ready for competition. Waiting for start.");
    this.define("examFinished", "Competition has finished. Redirect to home.");
    this.define("checkingForExam", "Trying to load tasks");
    this.define("cancelExamModeButton", "Dismiss");
    this.define("enterExamModeButton", "Try to load tasks");
    this.define("infoRecognition", "Access code required.");
    this.define("notauthorizedTitle", "Please log in");
    this.define("notauthorizedText", "In order to participate in a brain teaser, first log in.");
  }

  // Strings related to login
  private defineLoginStrings() {
    this.define("notingtosave", "Nothing to be saved.");
    this.define("howtosave", "Where would you like to store your document?");
    this.define("save", "Save");
    this.define("locally", "Local");
    this.define("online", "Online");
    this.define("load", "Load programs");
    this.define("noconnection", "Connection failed");
    this.define("filename", "Filename:");
    this.define("login", "Login: ");
    this.define("chapterx", "Chapter X Task Y");
    this.define("loadprogram", "Retrieve program:");
    this.define("chooseprogram", "Choose a program to load");
    this.define("youcanloadandstore", "Store and retrieve your programs online " + "\u2192" + " see menu");
    this.define("logoutsuccess", "logout successfull");
  }

  // These strings are static and do not need to be translated ever!
  populateStaticStrings() {
    this.define("XLogoOnline", "XLogoOnline");
  }
}



// WEBPACK FOOTER //
// ./src/app/languages/english.service.ts